import React from 'react';
import PropTypes from 'prop-types';
import {DatePickerInput, Toggle} from '@/components/atoms';
import {
  awardPointsToggleSchema,
  getActiveEndDateSchema,
  getActiveStartDateSchema,
  getDisplayEndDateSchema,
  getDisplayStartDateSchema,
  getRewardDateSchema,
  getSubmissionDateSchema,
  getValidationDateSchema,
  hasDisplayDateToggleSchema,
  hasValidationDateToggleSchema,
  VALIDATION_DATE_TOOLTIP
} from './schemas';

const cellClassName = 'w-full min-w-0 max-w-[296px] flex-none';
const rowClassName = 'flex flex-row flex-wrap gap-6';

const AvailabilitySection = ({
  formData,
  onChange,
  lockActiveStartDate = false,
  isPepIris = false
}) => {
  const activeStartDate = formData?.activeStartDate || '';
  const activeEndDate = formData?.activeEndDate;
  const showDisplayDates = !!formData?.hasDisplayDate;
  const showRewardDate = !formData?.awardPointsWhenTargetMet;
  const showValidationDate = isPepIris && !!formData?.hasValidationDate;

  const handleFieldChange = (field, value, validField, validValue) => {
    const updates = {...formData, [field]: value};
    if (validField !== undefined) {
      updates[validField] = validValue;
    }
    // Auto-populate Validation Date when Active Start Date changes
    if (
      isPepIris &&
      field === 'activeStartDate' &&
      formData?.hasValidationDate
    ) {
      updates.validationDate = value;
      updates.validationDateValid = validValue;
    }
    onChange(updates);
  };

  const handleAwardPointsToggleChange = (val) => {
    const updates = {
      ...formData,
      awardPointsWhenTargetMet: val
    };
    if (val) {
      updates.rewardDate = '';
      updates.rewardDateValid = undefined;
    }
    onChange(updates);
  };

  return (
    <div className='filters-partners' data-testid='perfect-store-availability'>
      <div
        className='flex flex-col gap-6'
        data-testid='perfect-store-availability-rows'>
        <div
          className={`${rowClassName} items-start`}
          data-testid='perfect-store-availability-dates-row'>
          <div className={cellClassName} data-testid='perfect-store-start-cell'>
            <DatePickerInput
              value={formData?.activeStartDate}
              updateValue={(val, validControl) =>
                handleFieldChange(
                  'activeStartDate',
                  val,
                  'activeStartDateValid',
                  validControl
                )
              }
              uiSchema={{
                ...getActiveStartDateSchema(activeEndDate),
                ...(lockActiveStartDate ? {disabled: true} : {})
              }}
            />
          </div>
          <div className={cellClassName} data-testid='perfect-store-end-cell'>
            <DatePickerInput
              value={formData?.activeEndDate}
              updateValue={(val, validControl) =>
                handleFieldChange(
                  'activeEndDate',
                  val,
                  'activeEndDateValid',
                  validControl
                )
              }
              uiSchema={getActiveEndDateSchema(activeStartDate)}
            />
          </div>
          {isPepIris && (
            <div
              className={cellClassName}
              data-testid='perfect-store-submission-cell'>
              <DatePickerInput
                value={formData?.submissionDate}
                updateValue={(val, validControl) =>
                  handleFieldChange(
                    'submissionDate',
                    val,
                    'submissionDateValid',
                    validControl
                  )
                }
                uiSchema={getSubmissionDateSchema(activeEndDate)}
              />
            </div>
          )}
          {showRewardDate && (
            <div
              className={cellClassName}
              data-testid='perfect-store-reward-cell'>
              <DatePickerInput
                value={formData?.rewardDate}
                updateValue={(val, validControl) =>
                  handleFieldChange(
                    'rewardDate',
                    val,
                    'rewardDateValid',
                    validControl
                  )
                }
                uiSchema={getRewardDateSchema(activeEndDate)}
              />
            </div>
          )}
        </div>

        <div
          className={`${rowClassName} items-center`}
          data-testid='perfect-store-availability-toggles-row'>
          <div
            className={cellClassName}
            data-testid='perfect-store-has-display-cell'>
            <Toggle
              value={!formData?.hasDisplayDate}
              updateValue={(val) => {
                const hasDisplayDate = !val;
                const updates = {
                  ...formData,
                  hasDisplayDate
                };
                if (!hasDisplayDate) {
                  updates.displayStartDate = '';
                  updates.displayEndDate = '';
                  updates.displayStartDateValid = undefined;
                  updates.displayEndDateValid = undefined;
                }
                onChange(updates);
              }}
              uiSchema={hasDisplayDateToggleSchema}
              id='has-display-date'
            />
          </div>
          {!isPepIris && (
            <div
              className={cellClassName}
              data-testid='perfect-store-award-cell'>
              <Toggle
                value={!!formData?.awardPointsWhenTargetMet}
                updateValue={handleAwardPointsToggleChange}
                uiSchema={awardPointsToggleSchema}
                id='award-points-when-met'
              />
            </div>
          )}
        </div>

        {showDisplayDates && (
          <div
            className={`${rowClassName} items-start`}
            data-testid='perfect-store-display-dates-row'>
            <div
              className={cellClassName}
              data-testid='perfect-store-display-start-cell'>
              <DatePickerInput
                value={formData?.displayStartDate}
                updateValue={(val, validControl) =>
                  handleFieldChange(
                    'displayStartDate',
                    val,
                    'displayStartDateValid',
                    validControl
                  )
                }
                uiSchema={getDisplayStartDateSchema(formData?.displayEndDate)}
              />
            </div>
            <div
              className={cellClassName}
              data-testid='perfect-store-display-end-cell'>
              <DatePickerInput
                value={formData?.displayEndDate}
                updateValue={(val, validControl) =>
                  handleFieldChange(
                    'displayEndDate',
                    val,
                    'displayEndDateValid',
                    validControl
                  )
                }
                uiSchema={getDisplayEndDateSchema(formData?.displayStartDate)}
              />
            </div>
          </div>
        )}

        {isPepIris && (
          <div
            className={`${rowClassName} items-center`}
            data-testid='perfect-store-validation-toggle-row'>
            <div
              className={cellClassName}
              data-testid='perfect-store-has-validation-cell'>
              <Toggle
                value={!!formData?.hasValidationDate}
                updateValue={(val) => {
                  const updates = {
                    ...formData,
                    hasValidationDate: val
                  };
                  if (val && activeStartDate) {
                    updates.validationDate = activeStartDate;
                  }
                  if (!val) {
                    updates.validationDate = '';
                    updates.validationDateValid = undefined;
                  }
                  onChange(updates);
                }}
                uiSchema={hasValidationDateToggleSchema}
                id='has-validation-date'
              />
            </div>
          </div>
        )}

        {showValidationDate && (
          <div
            className={`${rowClassName} items-start`}
            data-testid='perfect-store-validation-date-row'>
            <div
              className={cellClassName}
              data-testid='perfect-store-validation-cell'>
              <DatePickerInput
                value={formData?.validationDate}
                updateValue={(val, validControl) =>
                  handleFieldChange(
                    'validationDate',
                    val,
                    'validationDateValid',
                    validControl
                  )
                }
                uiSchema={getValidationDateSchema(activeStartDate)}
              />
              <p
                className='mt-1 text-xs text-gray-400'
                data-testid='validation-date-tooltip'>
                {VALIDATION_DATE_TOOLTIP}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

AvailabilitySection.propTypes = {
  formData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  lockActiveStartDate: PropTypes.bool,
  isPepIris: PropTypes.bool
};

export default React.memo(AvailabilitySection);
